import React from 'react';
import Icon from '@components/atoms/Icon';

interface ButtonProps {
  mode: 'link' | 'button';
  type?: 'submit' | 'button';
  className?: string;
  url?: string;
  buttonText: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary'; // Add variant prop
  icon?: string; // Name of the icon
  iconPosition?: 'left' | 'right'; // Allow positioning of the icon
  iconClassName?: string; // Additional classes for the icon
  disabled?: boolean; // Disabled state for the button
}

const Button: React.FC<ButtonProps> = ({
  mode,
  type = 'button',
  url,
  buttonText,
  className,
  onClick,
  variant = 'primary', // Default to primary if not provided
  icon,
  iconPosition = 'left', // Default icon position is left
  iconClassName, // Additional classes for the icon
  disabled = false, // Default disabled is false
}) => {
  const ButtonComponent = mode === 'link' && url ? 'a' : 'button';

  // Determine the class name based on the variant and disabled state
  const buttonClass = `button button--${variant} ${disabled ? 'button--disabled' : ''}`;

  return (
    <ButtonComponent
      type={ButtonComponent === 'button' ? type : undefined}
      className={`${className ? buttonClass + ' ' + className : buttonClass}`}
      href={ButtonComponent === 'a' && !disabled ? url : undefined} // Prevent navigation if disabled
      title={buttonText}
      onClick={!disabled ? onClick : undefined} // Prevent onClick if disabled
      disabled={ButtonComponent === 'button' ? disabled : undefined} // Apply disabled prop only for buttons
    >
      {/* Icon on the left */}
      {icon && iconPosition === 'left' && <Icon name={icon} className={`${iconClassName || ''}`} />}
      <div className="rectangle-1"></div>
      <div className="button-text">{buttonText}</div>
      {/* Icon on the right */}
      {icon && iconPosition === 'right' && <Icon name={icon} className={`${iconClassName || ''}`} />}
    </ButtonComponent>
  );
};

// Usage example
/**
 * Button component
 *
 * ```tsx
 * import Button from '@components/atoms/Button';
 *
 * Settings:
 *   mode: 'link' or 'button'
 *   type: 'submit' or 'button', for button mode (optional);
 *   url: URL for link mode (optional)
 *   buttonText: Text displayed on the button
 *   variant: 'primary' or 'secondary' (optional, default is 'primary')
 *   icon: Name of the icon (optional)
 *   iconPosition: 'left' or 'right' (optional, default is 'left')
 *   iconClassName: Additional class names for the icon (optional)
 *   disabled: Boolean to disable the button (optional, default is false)
 *
 * Usage:
 *   <Button mode="link" url="/collections/all" buttonText="Shop Now" icon="cart" disabled />
 *   <Button mode="button" buttonText="Learn More" variant="secondary" icon="heart" iconPosition="right" />
 * ```
 */

export default Button;
